import { BoardEvent, BoardType } from "@sportactics/board-editing";
import { create } from "zustand";

// `getState` rather than the hook: the store is a subscriber registry, not render
// state, so registering a subscriber must not re-render consumers — the same
// reason a component-local registry would live in a `useRef`. The actions are
// created once with the store, so their identities are stable across renders.
export function useBoardDomainEvents(): useBoardDomainEventsReturn {
  const { publish, subscribe } = useStore.getState();
  return { publish, subscribe };
}

export type useBoardDomainEventsReturn = {
  publish: (boardType: BoardType, events: readonly BoardEvent[]) => void;
  subscribe: (callback: BoardDomainEventsCallback) => () => void;
};

export type BoardDomainEventsCallback = (
  boardType: BoardType,
  events: readonly BoardEvent[],
) => Promise<void>;

// The subscriber sets are replaced, never mutated, so the initial state the test
// reset restores is always the empty set.
const useStore = create<StoreState>((set, get) => ({
  subscribers: new Set<BoardDomainEventsCallback>(),
  subscribe: (callback: BoardDomainEventsCallback) => {
    set((state) => ({ subscribers: new Set(state.subscribers).add(callback) }));
    return () =>
      set((state) => {
        const subscribers = new Set(state.subscribers);
        subscribers.delete(callback);
        return { subscribers };
      });
  },
  publish: (boardType: BoardType, events: readonly BoardEvent[]) => {
    get().subscribers.forEach((callback) => {
      callback(boardType, events);
    });
  },
}));

type StoreState = {
  subscribers: ReadonlySet<BoardDomainEventsCallback>;
} & useBoardDomainEventsReturn;
